import {
  bindAssignedElements,
  generateStepId,
  valueIsInRange,
  modifyPrice,
  escapeHtml
} from "./utils";
import { MeasurementOptionChangeEvent, Option, Pricing } from "./types";

import { CustomiserModelViewer } from "./customiser-model-viewer";
import { CustomiserImageSlider } from "./customiser-image-slider";
import { CustomiserSummaryBar } from "./customiser-summary-bar";
import { CustomiserSection } from "./customiser-section";
import { CustomiserColorPicker } from "./customiser-color-picker";
import { CustomiserVideoContent } from "./customiser-video-content";
import { CustomiserSizePicker } from "./customiser-size-picker";
import { CustomiserOptionPicker } from "./customiser-option-picker";
import { CustomiserOrderSummary } from "./customiser-order-summary";
import { CustomiserAdditionalInfo } from "./customiser-additional-info";
import { CustomiserAddCart } from "./customiser-add-cart";
import { CustomiserCartSuccess } from "./customiser-cart-success";
import "./customiser-tooltip";

import {
  OptionChangeEvent,
  ColorChangeEvent,
  DefaultColorChangeEvent,
  ProductData,
  ProductDataWithColors,
  DefaultStepOptionState,
  SelectedDimensions,
  DimensionSettings,
  StyleType,
  MaterialId,
} from "./types";

export const CLASS_OPEN = "is-bundle-builder-open";
export const CLASS_ADDING_TO_CART = "is-adding-custom-product";

const template = document.createElement("template");
template.innerHTML = `
  <style>
    :host {
      display: block;
    }

    :root {
      
    }

    :host(.close) {

    }

    :host(.o-bundle-builder) {

    }

    ::slotted(.o-bundle-builder__column) {
      width: 100%;
    }


    .o-customiser-engine {
      overflow: auto;
      position: relative;
      padding: 0;
      height: 100%;
      scrollbar-width: none;
    }

    .o-customiser-engine::-webkit-scrollbar {
      //display: none;
    }

    @media only screen and (min-width: 1100px) {
      .o-customiser-engine {
        width: calc(100% - 5rem);
        height: calc(100% - 5rem);
        display: grid;
        grid-template-columns: repeat(1fr, 12);
        padding: 5rem;
      }
    }

    @media only screen and (min-width: 1750px) {
      .o-customiser-engine {
        width: calc(100% - 6rem);
        height: calc(100% - 6rem);
        padding: 6rem;
      }
    }

  </style>

  <div class="o-customiser-engine" part="root">
    <slot></slot>
  </div>
`;

export class CustomiserEngine extends HTMLElement {
  private customiserElement: HTMLElement | null = null;

  private modelViewer: CustomiserModelViewer;
  private imageSlider: CustomiserImageSlider;
  private summaryBar: CustomiserSummaryBar;
  private sections: CustomiserSection[];
  private colorPickers: CustomiserColorPicker[];

  private videoContent: CustomiserVideoContent[];
  private sizePicker: CustomiserSizePicker;
  private optionPickers: CustomiserOptionPicker[];

  private orderSummary: CustomiserOrderSummary;
  private additionalInfo: CustomiserAdditionalInfo;
  private addToCart: CustomiserAddCart;
  private cartSuccess: CustomiserCartSuccess;

  private customiserId: string = this.getAttribute("customiser-id") ?? ""; // this is used when rendering the customiser in the cart drawer/page
  private newCustomiserId: string = "";

  private additionalInfoWrapper: HTMLElement | null = null;
  private finalAddToCart: HTMLElement | null = null;

  private schema;
  private presets;
  private hasDoublePreset: boolean;
  private product: ProductData;

  private doubleProduct: ProductDataWithColors | null = null;
  /**
   * IMPORTANT
   * In cases where an option has been selected, but then a change in application state
   * causes that option to no longer be a valid choice, (ie:user cannot select it due to lack of dependencies)
   * this maxTriggerIterations variable comes into play.
   *
   * If you find when the above case happens, and you are not seeing the expected options get selected,
   * you may need to increase this number
   */
  private maxTriggerIterations: number = 3;

  private room_name: string = "";
  private selected_dimensions: SelectedDimensions = { Width: 0, Drop: 0 };
  private icon_ratio = {
    width: 0,
    drop: 0,
    borderLength: 0,
  };
  // section ID key:option ID value
  private selectedOptions: { [key: string]: string } | {} = {};
  private selectedColorOptions: { [key: string]: {} } | {} = {};

  private productPricing: Pricing;
  private doubleProductPricing: Pricing | {} = {};

  private selectedDefaultColor: string;

  private addingToCartLabel: string;
  private continueShoppingUrl: string;

  constructor() {
    super();

    // const schemaData = this.getAttribute('schema')
    // if (!schemaData) console.error('Need to provide schema data attribute to ', this);

    const productData = this.getAttribute("product-data");
    if (!productData) console.error("Need to provide product-data attribute to ", this);

    this.product = JSON.parse(productData);
    this.schema = this.product.schema;
    this.presets = this.product.presets;
    this.hasDoublePreset = false;
    this.addingToCartLabel = this.product.adding_to_cart_label;
    this.continueShoppingUrl = this.product.continue_shopping_url;
    this.productPricing = {
      pricing: this.product.pricing,
      sale_price_matrix: this.product.sale_price_matrix,
      enable_sale_prices: this.product.enable_sale_prices,
      use_sale_price_matrix: this.product.use_sale_price_matrix,
      discount: this.product.discount,
    };

    const shadowRoot = this.attachShadow({ mode: "open" });
    shadowRoot.appendChild(template.content.cloneNode(true));

    const slotElement = this.shadowRoot.querySelector("slot");
    const getSubComponent = bindAssignedElements(slotElement!);

    this.customiserElement = this.shadowRoot.querySelector(".o-customiser-engine");

    this.modelViewer = getSubComponent<CustomiserModelViewer>("customiser-model-viewer")![0]!;
    this.imageSlider = getSubComponent<CustomiserImageSlider>("customiser-image-slider")![0]!;
    this.sizePicker = getSubComponent<CustomiserSizePicker>("customiser-size-picker")![0];
    this.summaryBar = getSubComponent<CustomiserSummaryBar>("customiser-summary-bar")![0];
    this.sections = getSubComponent<CustomiserSection[]>("customiser-section")!;
    this.colorPickers = getSubComponent<CustomiserColorPicker[]>("customiser-color-picker")!;
    this.videoContent = getSubComponent<CustomiserVideoContent[]>("customiser-video-content")!;
    this.optionPickers = getSubComponent<CustomiserOptionPicker[]>("customiser-option-picker")!;
    this.orderSummary = getSubComponent<CustomiserOrderSummary>("customiser-order-summary")![0];
    this.additionalInfo = getSubComponent<CustomiserAdditionalInfo>("customiser-additional-info")!;
    this.addToCart = getSubComponent<CustomiserAddCart>("customiser-add-cart")![0];
    this.cartSuccess = getSubComponent<CustomiserCartSuccess>("customiser-cart-success")![0];    
    this.additionalInfoWrapper = this.querySelector<HTMLElement>(".o-customiser__additional-info-wrapper");
    this.finalAddToCart = this.querySelector<HTMLButtonElement>(".add-to-cart");

    this.addEventListener("color_change", this.handleColorChange);
    this.addEventListener("option_change", this.handleOptionChange);
    this.addEventListener("measurement_option_change", this.handleMeasurementOptionChange);
    document.addEventListener("size_change", this.handleSizeChange.bind(this));
    this.addEventListener("room_name_change", this.handleRoomNameChange);
    this.addEventListener("add_another", this.handleAddAnother.bind(this));
    this.addEventListener("close_customiser", this.closeCustomiser.bind(this));
    this.addEventListener("scroll_parent", this.handleScrollParent);
    this.addEventListener("add_to_cart", this.handleAddToCart);
    document.addEventListener("default_color_change", this.handleDefaultColorChange.bind(this));

    this.setEventListeners();
    this.setIntersectionObservers();
    if(!this.presets) this.reset();
  }

  /**
   * HANDLERS
  */

  private setEventListeners() {
    // Set Upgrade Event Listner
    document.addEventListener("upgrade_customiser", (e) => {
      const customEvent = e as CustomEvent;
      const thumbSchema = customEvent.detail.schema;
      if (this.id === customEvent.detail.id) {
        this.onUpgradeProduct(e, thumbSchema);
      }
    });

    // Set Launch Customiser Event Listner
    document.addEventListener("open_customiser", (e) => {
      const customEvent = e as CustomEvent;
      if (this.id === customEvent.detail.id) {
        this.onLaunchCustomiser(e);
      }
    });
  }

  // Preload Settings
  private loadPresets() {
    if (this.presets) {
      this.addToCart.setAddToCartLabel("Update product", true);
    }

    // Check if we are editing a product, and remove edit query string.
    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (params.has('edit')) {
      params.delete('edit');
      window.history.replaceState({}, '', url.toString());
    }

    let isDouble = false;

    // Find the element closest to the current customiserElement
    const presetDoubleSchema = document.querySelector(`[data-preset-upsell-schema="${this.getAttribute('id')}"]`) as HTMLScriptElement;

    if (presetDoubleSchema) {
      this.hasDoublePreset = true;
      const customEvent = new Event('null');
      this.onUpgradeProduct(customEvent, presetDoubleSchema);
    }

    // Curtains
    const colour = this.presets["Colour"];
    const blockoutColour = String(this.presets["Blockout Colour"]);
    const blockoutPleating = this.presets["Blockout Pleating"];
    let curveMeasurement = this.presets["Curve Measurement"];
    const notes = this.presets["Notes"];
    const orderNotes = this.presets["Order Notes"];
    const room = this.presets["Room"];
    const sheerColour = this.presets["Sheer Colour"];
    const sheerPleating = this.presets["Sheer Pleating"];
    const size = this.presets["Size"];

    // Shutters
    const midrailPosition = this.presets["Midrail Position"];
    const splitTiltPosition = this.presets["Position of split-tilt"];
    const blockoutLining = this.presets["Blockout Lining"];

    if (sheerColour) {
      isDouble = true
    }

    // Split the size into width and height
    const sizeParts = size?.split("×");
    let width = '';
    let drop = '';

    if (sizeParts) {
      width = sizeParts[0].trim();
      drop = sizeParts[1].replace("mm", "").trim();
    }
    
    // Split cureveParts into Left and Right Walls
    const curveParts = curveMeasurement?.split(",");
    let leftWall = '';
    let rightWall = '';

    if (curveParts) {
      leftWall = curveParts[0].replace("Left Wall: ", "").trim();
      rightWall = curveParts[1].replace("Right Wall: ", "").trim();
    }


    // COLOUR FIELDS
    if (colour) {
      this.colorPickers?.forEach(p => {
        const colorSwatches = p?.querySelectorAll("customiser-color");
        colorSwatches?.forEach(i => {
          if (i.getAttribute('data')?.includes(`${colour}`)) {
            const input = i?.shadowRoot?.querySelector('input')
            if (input) input.click();
          }
        });
      })
    }

    if (blockoutColour && isDouble) {
      this.colorPickers?.forEach(p => {
        const colorSwatches = p?.querySelectorAll("customiser-color");
        colorSwatches?.forEach(i => {
          if (i.getAttribute('data')?.includes(`${blockoutColour}`)) {
            const input = i?.shadowRoot?.querySelector('input')
            if (input) input.click();
          }
        });
      })
    }

    if (sheerColour && isDouble) {
      this.colorPickers?.forEach(p => {
        const colorSwatches = p?.querySelectorAll("customiser-color");
        colorSwatches?.forEach(i => {
          if (i.getAttribute('data')?.includes(`${sheerColour}`)) {
            const input = i?.shadowRoot?.querySelector('input')
            if (input) input.click();
          }
        })
      });
    }

    // ROOM AND SIZES
    const inputEvt = new Event('input');
    const nameField = this.sizePicker.shadowRoot?.querySelector('input[name="Room Name"') as HTMLInputElement;
    const sizePickerEl = this.sizePicker.shadowRoot?.querySelector('size-picker');
    const widthField = sizePickerEl?.shadowRoot?.querySelector('input[name="Width"') as HTMLInputElement;
    const dropField = sizePickerEl?.shadowRoot?.querySelector('input[name="Drop"') as HTMLInputElement;

    if (nameField) nameField.value = room, nameField.dispatchEvent(inputEvt);
    if (widthField) widthField.value = width, widthField.dispatchEvent(inputEvt);
    if (dropField) dropField.value = drop, dropField.dispatchEvent(inputEvt);

    const optionsToCheck = {
      "Curve Measurment": String(curveMeasurement),
      "Notes": String(notes).replace("Anything else we should know?: ", "").replace('undefined',''),
      "Order Notes": String(orderNotes).replace("Anything else we should know?: ", "").replace('undefined',''),
      "Midrail Position": String(midrailPosition).replace("Height in mm: ", ""),
      "Position of split-tilt": String(splitTiltPosition).replace("Height in mm: ", "")
    };

    this.optionPickers.forEach((optPicker) => {
      if (optPicker.classList.contains("is-hidden")) return;
      const titleStep = optPicker.parentElement?.shadowRoot?.querySelector('.o-customiser__step-title')?.innerHTML;
      const measurementOptions = optPicker.querySelectorAll('customiser-measurement-option');
      const optionButtons = optPicker.querySelectorAll('customiser-option');
      let hasSelectedOption = false;

      optionButtons?.forEach(o => {
        if (o.classList.contains("is-hidden")) return;
        const title = o.shadowRoot?.querySelector('.title')?.innerHTML;
        const swatch = o.shadowRoot?.querySelector('input')
        const parentStepTitle = o.getAttribute('step-title');

        if (titleStep === "Blockout Pleating" && title === blockoutPleating) {
          swatch?.click();
          hasSelectedOption = true;
        } else if (titleStep === "Sheer Pleating" && title === sheerPleating) {
          swatch?.click();
          hasSelectedOption = true;
        } else if (titleStep === "Blockout Lining" && title === blockoutLining) {
          swatch?.click();
          hasSelectedOption = true;
        } else if (parentStepTitle && this.presets[parentStepTitle] && title == this.presets[parentStepTitle]) {
          swatch?.click();
          hasSelectedOption = true;
        }
      })

      // Select first option for hidden steps
      if (!hasSelectedOption && optionButtons.length) {
        let selectedCount = 0;

        optionButtons?.forEach(o => {
          const swatch = o.shadowRoot?.querySelector('input')
          if (o.classList.contains("is-hidden")) return;
          if (selectedCount == 0) swatch?.click();
          selectedCount++;
        })
      }

      measurementOptions?.forEach(o => {
        if (o.classList.contains("is-hidden")) return;
        const inputs = o.querySelectorAll('input');
        inputs.forEach(input => {
          const inputEl = input as HTMLInputElement;
          if (inputEl.name === "Left Wall*") {
            inputEl.value = leftWall;
            inputEl.dispatchEvent(inputEvt);
          } else if (inputEl.name === "Right Wall*") {
            inputEl.value = rightWall;
            inputEl.dispatchEvent(inputEvt);
          } else {
            Object.keys(optionsToCheck).forEach((key) => {
              if (titleStep === key) {
                inputEl.value = String(optionsToCheck[key]);
                inputEl.dispatchEvent(inputEvt);
              }
            });
          }
        });
      });
    });
  }


  // Setup Intersection Observers
  private setIntersectionObservers() {
    // Variables to track intersection states
    let isOrderSummaryInView = false;
    let isAdditionalInfoInView = false;

    const observerCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.target === this.orderSummary) {
          isOrderSummaryInView = entry.isIntersecting;
        } else if (entry.target === this.additionalInfoWrapper) {
          isAdditionalInfoInView = entry.isIntersecting;
        } 

        // Check if either element is in view
        if (isOrderSummaryInView || isAdditionalInfoInView) {
          this.imageSlider?.classList.add("summary-in-view");
          this.modelViewer?.classList.add("summary-in-view");
          this.summaryBar?.classList.add("summary-in-view");
          this.addToCart?.classList.add("summary-in-view");
        } else {
          this.imageSlider?.classList.add("summary-in-view");
          this.modelViewer?.classList.remove("summary-in-view");
          this.summaryBar?.classList.remove("summary-in-view");
          this.addToCart?.classList.remove("summary-in-view");
        }
      });
    };

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    if (this.orderSummary) {
      observer.observe(this.orderSummary);
    }

    if (this.additionalInfoWrapper) {
      observer.observe(this.additionalInfoWrapper);
    }
  }

  // Scroll customiser to section.
  private handleScrollParent(e) {
    const sectionToScrollTo = this.getStepById(e.detail.stepId);
    if (!sectionToScrollTo) return;

    const offset = sectionToScrollTo.offsetTop;
    this.customiserElement?.scrollTo({
      top: offset,
      behavior: "smooth",
    });

    if (e.detail.validateFields) {
      this.sizePicker.validateFields();
    }
  }

  // Open the Customiser
  private openCustomiser() {
    this.classList.add("is-open");
    document.body.classList.add("is-scroll-disabled");
    this.tidyQueryStrings(); // Tidy up query strings
  }

  // Close the Customiser
  private closeCustomiser() {
    this.classList.remove("is-open");
    document.body.classList.remove("is-scroll-disabled");
  }

  // Show Cart Success Modal
  private showCartSuccess() {
    if (this.presets) {
      location.reload();
    } else {
      this.cartSuccess.openCartSuccess();
    }
  }

  // Add another window
  private handleAddAnother() {
    // Close the cart success modal
    this.cartSuccess.closeCartSuccess();
    this.scrollToSection(this.firstSection.step_id);
  }

  private handleAddToCart(e: Event) {
    e.preventDefault();
    // Room name entered
    const roomNameEntered = !!this.room_name;

    // Measurements valid
    const widthValid = valueIsInRange(
      this.selected_dimensions.Width,
      this.dimensionConfig.Width.min,
      this.dimensionConfig.Width.max
    );
    const dropValid = valueIsInRange(
      this.selected_dimensions.Drop,
      this.dimensionConfig.Drop.min,
      this.dimensionConfig.Drop.max
    );

    if (!roomNameEntered || !widthValid || !dropValid || Number.isNaN(parseInt(this.calculatedPrice))) {
      this.scrollToSection("CRTSZE", true);
      return;
    }

    // Measurement options valid
    if (!this.invalidMeasurementOptions.length) {

      (async () => {

        if (this.presets) {
          this.addToCart.setAddToCartLabel("Updating...", false);
          this.addingToCartLabel = 'Updating...';
        } else {
          this.addToCart.setAddToCartLabel("Adding...", false);
        }

        document.body.classList.add(CLASS_ADDING_TO_CART);
        this.cartSuccess.openCartAdding(this.addingToCartLabel, this.continueShoppingUrl);

        // send request to JAM
        const variantId = await this.sendProductToJAM();

        let completeColour;

        // Get line item properties
        let properties = this.formattedSummary.reduce((acc, item) => {
          if (acc[item.title] == "Colour") {
            const completeColour = item.value;
            const colorNameSplit = completeColour.split("-");
            const sanitizedColor =
              colorNameSplit[colorNameSplit.length - 1].trim();
            acc[item.title] = sanitizedColor;
          } else {
            acc[item.title] = item.value;
          }
          return acc;
        }, {});

        properties._colour = completeColour;
        properties._base_product_url = `${this.product.base_product_url}`;
        properties['Room Name'] = this.room_name;

        if (variantId) {
          console.log("Product added successfully", variantId);

          // create variant ID to cart w/ LI props
          // setTimeout is to give time for SHopify to Process Images first to ensure there's a product thumbnail inside cart

          let quantity = 1;

          const itemKey = this.getAttribute('data-item-key');
          const cartItemQuantity = document.querySelector(`[data-cart-key='${itemKey}'] [data-quantity-selector-field]`) as HTMLInputElement;

          if (cartItemQuantity) {
            quantity = Number(cartItemQuantity.value);
          }
          
          // Remove Old Items from cart
          const removeFromCart = async (itemKey) => {
            try {
              const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  id: itemKey,
                  quantity: 0
                }),
              });
              const data = await response.json();
              console.log('Item removed from cart:', data);
              return data;
            } catch (error) {
              console.error('Error removing item from cart:', error);
              throw error;
            }
          };

          if (itemKey) await removeFromCart(itemKey);

          await new Promise((resolve) => setTimeout(resolve, 2000));
          await this.addToCart.addItemToCart(variantId, properties, quantity);

          this.showCartSuccess();

          setTimeout(() => {
            document.body.classList.remove(CLASS_ADDING_TO_CART);
          }, 1000);

        } else {
          console.error("Failed adding product.");

          if (this.presets) {
            this.addToCart.setAddToCartLabel("Update product", false);
          } else {
            this.addToCart.setAddToCartLabel("Add to cart", false);
          }
        }
      })();
    } else {
      // Only scroll to the first field that has an error
      const measurementOption = this.invalidMeasurementOptions[0].querySelector("customiser-measurement-option");
      const stepId = measurementOption.step_id;
      this.scrollToSection(stepId, false);
    }
  }

  async sendProductToJAM() {
    console.log("sending product to JAM...");
    const itemKey = this.getAttribute('data-item-key');
    const url = "https://jam-cbco-production.pro-cess.cc/add-to-cart";
    // const headers = {
    //   'Content-Type': 'application/json',
    //   'Authorization': "Basic SkFNX0NCQ09fUFJPRFVDVElPTl9GUk9OVEVORDpPVXEycmRJcDk5bjJ3NGxPcDRscg==",
    //   'X-Jam-Auth': "Basic SkFNX0NCQ09fUFJPRFVDVElPTl9GUk9OVEVORDpPVXEycmRJcDk5bjJ3NGxPcDRscg=="
    // };

    const currentURL = window.location.href;
    let token;

    if (currentURL.includes("cbc01-curtain-blind-co-site")) {
      token =
        "Basic SkFNX0NCQ09fREVWRUxPUE1FTlRfRlJPTlRFTkQ6T1VxMnJkSXA5OW4ydzRsT3A0bHI=";
    } else {
      token =
        "Basic SkFNX0NCQ09fUFJPRFVDVElPTl9GUk9OVEVORDpPVXEycmRJcDk5bjJ3NGxPcDRscg==";
    }

    let productImage = document.querySelector(".c-product-gallery__carousel img")?.getAttribute("src");
    
    if (itemKey && !productImage) {
      productImage = document.querySelector(`[data-cart-key="${itemKey}"] .c-cart-product__image`)?.getAttribute("src"); 
    }

    const productTitle = this.isDoubleConfig ? `${this.product.title} & ${this.doubleProduct.title} Double` : this.product.title; 
    const productDetails = {
      price: this.calculatedPrice,
      title: productTitle,
      product_image: productImage,
      room_name: this.room_name,
      selections: this.formattedSummary,
      requestToken: token,
      base_product_url: this.product.base_product_url,
      tags: this.product.tags,
      collections: this.product.collections
    };

    const body = JSON.stringify(productDetails);

    try {
      const response = await fetch(url, {
        method: "POST",
        // headers: headers,
        body: body,
      });

      if (response.ok) {
        const data = await response.json();
        console.log("API call successful:", data);
        return data.variantId;
      } else {
        const errorData = await response.json();
        console.error("API call failed:", errorData);
        return null;
      }
    } catch (error) {
      console.error("Error making API call:", error);
      return null;
    }
  }

  private handleMeasurementOptionChange(e: MeasurementOptionChangeEvent) {
    const { step_id, input_id, value, field_index } = e.detail;
    if (!this.selectedOptions[step_id]) {
      this.selectedOptions[step_id] = {};
    }
    this.selectedOptions[step_id][input_id] = { value, field_index };
    // this.checkDependencies(); // May not need this as editing these options doesnot have an impact on showinghiding other options
    this.updateUI();

    const invalidFields = this.invalidMeasurementOptions.length;
  }

  private handleOptionChange(e: OptionChangeEvent) {
    const { option_id, step_id, triggerIteration } = e.detail;
    // add selected option to list
    this.selectedOptions[step_id] = option_id;

    // Check any dependencies
    if (triggerIteration < this.maxTriggerIterations) {
      // Hide/show/disable/able options and steps
      this.checkDependencies(triggerIteration);
    }

    this.updateUI();
  }

  private handleSizeChange(e) {
    this.selected_dimensions = e.detail.values;
    this.icon_ratio = e.detail.icon_ratio;
    this.checkDependencies();
    this.updateUI();
  }

  private handleRoomNameChange(e) {
    this.room_name = e.detail;
    this.updateUI();
  }

  private handleDefaultColorChange(e: DefaultColorChangeEvent) {
    const { variant_id } = e.detail;
    this.setDefaultColor(variant_id);
  }

  private setDefaultColor(variant_id:string) {
    this.selectedDefaultColor = variant_id;
    this.colorPickers.forEach((colorPicker) => {
      colorPicker.setSelectedColor(variant_id);
    });
  }

  private handleColorChange(e: ColorChangeEvent) {
    const {
      option_id,
      step_id,
      option_title,
      sample_variant_id,
      sample_variant_availability,
      hem,
      style_type,
      image,
      default_variant_id,
      variant_id,
      sample_disable_message,
      slider_data
    } = e.detail;

    // add selected option to list
    this.selectedColorOptions[step_id] = {
      option_id,
      option_title,
      sample_variant_id,
      sample_variant_availability,
      hem,
      style_type,
      image,
      default_variant_id,
      variant_id,
      sample_disable_message,
      slider_data
    };

    this.updateUI();
    this.imageSlider?.updateSlider(slider_data);
  }

  /**
   * UI UPDATES
   */

  private bindComponentsAfterSchemaUpdate() {
    const slotElement = this.shadowRoot.querySelector("slot");
    const getSubComponent = bindAssignedElements(slotElement!);

    this.optionPickers = getSubComponent<CustomiserOptionPicker[]>(
      "customiser-option-picker"
    )!;
    this.sections = getSubComponent<CustomiserSection[]>("customiser-section")!;
    this.colorPickers = getSubComponent<CustomiserColorPicker[]>(
      "customiser-color-picker"
    )!;
    // this.colorPickers.forEach(picker => picker.reset())
    // this.sizePicker = getSubComponent<CustomiserSizePicker>('customiser-size-picker')!;
  }

  private removeSections(sectionsToRemove) {
    sectionsToRemove.forEach((section) => {
      section.remove();
    });
  }

  private tidyQueryStrings() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
  
    // Check for specific query strings and remove them
    if (params.has('double_customiser') || params.has('single_customiser')) {
      params.delete('double_customiser');
      params.delete('single_customiser');
  
      // Update the URL without reloading the page
      history.replaceState(null, '', url.toString());
    }
  }

  private updateUI() {
    // Update step indexes
    this.setStepIndexes();
    // Update step option UI
    this.setOptPickers();
    // Update color option UI
    this.setColorPickers();
    // Update price UI

    // Update image UI
    if (this.modelViewer) this.setModel();

    // Update summary UI
    this.setOrderSummary();
  }

  private setOrderSummary() {
    // Get Order Summary Data
    const summaryData = this.formattedSummary;
    this.orderSummary.updateOrderSummary(summaryData);
    this.summaryBar.updateSummaryBar(this.calculatedPrice, summaryData);
    this.addToCart.updateFinalPrice(this.calculatedPrice);
  }

  private get activeSectionWithOptionPickerStepIds() {
    return this.stepsWithOptionPickers.filter(
      (section) => section.current_state === "Visible"
    );
  }

  private setStepIndexes() {
    this.activeSections.forEach((section, index) => {
      section.setStepIndex(index);
    });
  }

  private setColorPickers() {
    this.colorPickers.forEach((colorPicker) => {
      const stepId = this.colorPickerStepIds.find(
        (stepId) => stepId === colorPicker.step_id
      );
      const colorOptionForStep = this.selectedColorOptions[stepId];
      if (colorOptionForStep) colorPicker.setOption(colorOptionForStep);
    });
  }

  private setOptPickers() {
    this.optionPickers.forEach((optPicker) => {
      const stepId = this.selectedOptionStepIds.find(
        (stepId) => stepId === optPicker.step_id
      );
      const optIdForStep = this.selectedOptions[stepId];
      if (optIdForStep) optPicker.setOption(optIdForStep);
    });
  }

  private setModel() {
    // Product
    // product type Model ID - Comes from STYLE Resource
    const ProductTypeModelId = this.product.product_type_model_id;
    
    const fabricFieldIds  = this.modelViewer.getFabricFieldIdsForProductType(ProductTypeModelId, this.isDoubleConfig);
    const MaterialFabricsAndHem = this.isDoubleConfig ? this.doubleProductMatFabHem(fabricFieldIds) : this.singleProductMatFabHem(fabricFieldIds);
    // selected options
    // get all of the Option model IDs with the Model Field ID
    const configuredOptions = this.optionPickersWithModelIdFields.reduce((acc, optPicker) => {
      const modelIdField = optPicker.model_id_field;
      // get the selected option's model ID
      return { 
        ...acc, 
        [modelIdField]: optPicker.selectedOptions?.model_id
      }
    }, {});

    this.modelViewer.updateModel({
      isDoubleConfig: this.isDoubleConfig,
      ProductTypeModelId,
      ...MaterialFabricsAndHem,
      ...configuredOptions,
    });
  }

  /*** 
    RESET 
  ***/
  reset() {
    this.selected_dimensions = {
      Width: 0,
      Drop: 0,
    };
    this.selectedOptions = {};
    this.selectedColorOptions = {};
    this.colorPickers.forEach((picker) => picker.reset());

    this.sizePicker.reset(this.dimensionConfig);
    this.optionPickers.forEach((picker) => picker.reset());
    this.updateUI();
  }

  /**
   * VALIDATIONS
   */

  get invalidMeasurementOptions() {
    // need to refactor this at some point
    // try and move the getValueForIndex, allvalidationmethods methods out of this scope

    // ALSO
    // Need to change this to only check the active option pickers
    const invalidFieldsList = this.optionPickersWithMeasurementOptions.filter((optPicker) => {
      const measurementOption = optPicker.querySelector("customiser-measurement-option");
      if (!measurementOption) return true;

      const validationRules = measurementOption.validationParams;
      // if no validation needed, return false
      const needsValidation = Object.keys(validationRules).reduce((acc, curr) => {
        if (acc) return acc
        return validationRules[curr]
      }, false);

      if (!needsValidation) return needsValidation;

      const stepId = measurementOption.step_id;

      const selectedValues = this.selectedOptions[stepId];
      if (!selectedValues) return true;

      const getValueForIndex = (index) => {
        const fieldKey = Object.keys(selectedValues).find((field_key) => {
          return selectedValues[field_key].field_index === index;
        });

        return selectedValues[fieldKey];
      };

      const valueForIndex1 = getValueForIndex(0)?.value;
      const valueForIndex2 = getValueForIndex(1)?.value;

      const allValidationMethods = {
        field1_required: () => {
          return !!valueForIndex1.length;
        },
        field2_required: () => {
          return !!valueForIndex2.length;
        },
        field_validation_equals_max_width: () => {
          // Get the values and add them up
          const valuesAdded = Object.keys(selectedValues).reduce(
            (acc, field_key) => {
              const fieldValue = parseInt(selectedValues[field_key].value);
              return (acc += fieldValue);
            },
            0
          );

          // And make sure they equal the width entered
          return valuesAdded === this.selected_dimensions.Width;
        },
      };  

      const validationsNeeded = Object.keys(allValidationMethods).filter((validation_key) => {
        return validationRules[validation_key];
      });

      const invalidFields = validationsNeeded.filter((validation_key) => {
        const fieldIsValid = allValidationMethods[validation_key](selectedValues);
        return !fieldIsValid;
      });

      return !!invalidFields.length;
    });

    if (invalidFieldsList.length) {
      invalidFieldsList.forEach((optionPicker, index) => {
        optionPicker.triggerError(true);
      });
    } else {
      this.optionPickersWithMeasurementOptions.forEach((optionPicker, index) => {
        optionPicker.triggerError(false);
      });
    }

    return invalidFieldsList;
  }

  scrollToSection(stepId, validateFields:boolean=false) {
    const evt = new CustomEvent("scroll_parent", {
      detail: {
        stepId: stepId,
        validateFields: validateFields,
      },
      bubbles: true,
    });

    this.dispatchEvent(evt);
    return;
  }

  /**
   * DEPENDENCY
   */
  get allDependencies() {
    return [...this.stepDependencies, ...this.optionDependencies];
  }

  get stepDependencies() {
    return this.sections
      .filter((section) => section.dependencies)
      .map((section) => {
        return {
          step_id: section.step_id,
          dependencies: section.dependencies,
        };
      });
  }

  get optionDependencies() {
    return this.optionPickers
      .filter((optPicker) => optPicker.dependencies)
      .map((optPicker) => optPicker.dependencies)
      .flat();
  }

  get triggeredActions() {
    return this.allDependencies.map((dep) => {
      const { step_id, option_id, dependencies } = dep;

      const { idGroups, measurementDependency, action, rule_connection } = dependencies;
      const triggerActionIdGroups = idGroups?.map((orGroup) => {
          return orGroup.some((optionId) => this.selectedOptionIds.includes(optionId));
        })
        .reduce((acc, orGroupValid) => acc && orGroupValid, true);

      const triggerActionMeasurement = measurementDependency ? 
        (() => {
          const { dimension, values } = measurementDependency;
          const aboveLowerThreshold =
            this.selected_dimensions[dimension] >= values.lower;
          const belowUpperThreshold =
            this.selected_dimensions[dimension] <= values.upper;

          return aboveLowerThreshold && belowUpperThreshold;
        })()
      : true;

      const triggerAction: boolean = (() => {
        if (!measurementDependency) return triggerActionIdGroups;

        return rule_connection === "Or"
          ? triggerActionIdGroups || triggerActionMeasurement
          : triggerActionIdGroups && triggerActionMeasurement;
      })();

      return {
        triggerAction,
        action,
        step_id,
        option_id,
      };
    });
  }

  private handleDependencyOptionSelection(
    triggerIteration: number,
    optionId: string
  ) {
    const optionPicker = this.getOptionPickerForOptionId(optionId);
    optionPicker?.selectNextAvailableOption(triggerIteration);
  }

  private handleDependencyStepSelection(
    stepId: string,
    state: DefaultStepOptionState
  ) {
    const step = this.getStepById(stepId);
    step?.setState(state);
  }

  private checkDependencies(triggerIteration: number = 1) {
    this.triggeredActions.forEach((action) => {
      const isStep = !!action.step_id;
      const actionTarget = isStep
        ? this.getStepById(action.step_id)
        : this.getOptionById(action.option_id);
      const newState = action.triggerAction
        ? action.action
        : actionTarget.default_state;

      actionTarget?.setState(newState);

      if (newState !== "Visible" && actionTarget.isSelected) {
        const idToTarget = isStep ? action.step_id : action.option_id;
        const funcToFire = !isStep
          ? () =>
              this.handleDependencyOptionSelection(triggerIteration, idToTarget)
          : () => this.handleDependencyStepSelection(idToTarget, newState);
        funcToFire();
      }
    });
  }

  /**
   * DOUBLE PRODUCT
   */

  get isDoubleConfig() {
    return !!this.doubleProduct;
  }

  // Handle Upsell Thumbnail Actions
  public onLaunchCustomiser(e: Event) {
    e.preventDefault();

    // Get the 'variant' query string value from the URL
    const urlParams = new URLSearchParams(window.location.search);
    const variant = urlParams.get('variant');

    if (this.isDoubleConfig) this.removeDoubleSchema();

    // If 'variant' is present in the URL, call setDefaultColor with it
    if (variant) this.setDefaultColor(variant);

    this.openCustomiser();

    if (this.presets) {
      this.loadPresets();
    }
  }

  // Handle Upsell Thumbnail Actions
  public onUpgradeProduct(e: Event, thumbSchema: Element) {
    e.preventDefault();

    // Get the 'variant' query string value from the URL
    const urlParams = new URLSearchParams(window.location.search);
    const variant = urlParams.get('variant');

    const el = e.currentTarget as HTMLElement;
    const elModal = document.querySelector('[data-modal][id="upgrade-and-save"]');
    elModal?.classList.remove("is-open");
    const newProduct = JSON.parse(thumbSchema.textContent!.trim());
    if (this.isDoubleConfig) this.removeDoubleSchema();
    this.loadDoubleSchema(newProduct);
    this.openCustomiser();
    if (variant) this.setDefaultColor(variant);
  }

  public removeDoubleSchema() {
    this.doubleProduct = null;
    this.doubleProductPricing = {};

    // Get original ColorPickerSection
    const originalColourPickerStepId = this.colorPickers[0].step_id;
    const originalColorPickerSection = this.getStepById(
      originalColourPickerStepId
    );
    originalColorPickerSection?.setStepData({
      name: "Colour",
      title: "Choose Colour",
    });

    const stepsWithOptionPickers = this.stepsWithOptionPickers;
    const stepBeforeOptionPickers = this.stepBeforeOptionPickers;

    const doubleColourPickerStepId = this.colorPickers[1].step_id;
    const doubleColorPickerSection = this.getStepById(doubleColourPickerStepId);
    this.removeSections([doubleColorPickerSection, ...stepsWithOptionPickers]);

    this.renderOptionPickerSteps(this.product.schema, stepBeforeOptionPickers);

    this.reloadSchema();
  }

  private reloadSchema() {
    this.bindComponentsAfterSchemaUpdate();
    this.updateOptionPricing();
    this.reset();
  }

  public setDoubleProduct(product: ProductDataWithColors | null) {
    this.doubleProduct = product;
    this.doubleProductPricing = this.doubleProduct
      ? {
          pricing: this.doubleProduct.pricing,
          sale_price_matrix: this.doubleProduct.sale_price_matrix,
          enable_sale_prices: this.doubleProduct.enable_sale_prices,
          use_sale_price_matrix: this.doubleProduct.use_sale_price_matrix,
          discount: this.doubleProduct.discount,
        }
      : {};
  }

  public loadDoubleSchema(newProduct: ProductDataWithColors) {
    if (this.isDoubleConfig)
      return console.warn("A double schema is already loaded!");
    this.setDoubleProduct(newProduct);
    const originalProductType = this.product.style_type;
    const doubleProductType = this.doubleProduct?.style_type;

    // Get original ColorPickerSection
    const originalColourPickerStepId = this.colorPickers[0].step_id;
    const originalColorPickerSection = this.getStepById(
      originalColourPickerStepId
    );

    originalColorPickerSection?.setStepData({
      name: `${originalProductType} Colour`,
      title: `Choose ${originalProductType} Colour`,
    });

    // new Colour picker data
    const colorPickerTitle = `Choose ${doubleProductType} Colour`;
    const colorPickerStepId = generateStepId(colorPickerTitle);
    const colorPicker = this.createColorPicker(
      colorPickerTitle,
      colorPickerStepId,
      doubleProductType,
      this.doubleProduct.colors
    );
    const colorSection = this.createSection({
      index: originalColorPickerSection!.stepIndex,
      stepName: `${doubleProductType} Colour`,
      stepTitle: colorPickerTitle,
      stepId: colorPickerStepId,
      child: colorPicker,
    });
    originalColorPickerSection?.insertAdjacentHTML("afterend", colorSection);

    const stepsWithOptionPickers = this.stepsWithOptionPickers;
    const stepBeforeOptionPickers = this.stepBeforeOptionPickers;
    this.removeSections(stepsWithOptionPickers);

    const newSteps = this.formatCustomiserSteps(this.doubleProduct.schema);
    this.renderOptionPickerSteps(newSteps, stepBeforeOptionPickers);
    this.reloadSchema();
  }

  private updateOptionPricing() {
    this.allOptionsWithCost.forEach((option) => {
      // If it's a double config, then we need to increase the cost
      const newCost = this.isDoubleConfig ? parseInt(option.cost) * 2 : option.cost;
      option.setCost(newCost);
    });
  }

  private updateDuplicateOptions(step) {
    return step.options
      .map((option) => {
        const [oldStepId, optionIdSuffix] = option.option_id.split("_");
        option.option_id = `${step.step_id}_${optionIdSuffix}`;
        return option;
      })
      // .filter((option) => !option.hide_on_double_configuration);
  }

  private formatDuplicateSteps(step, stepToDuplicate) {
    const product1Type = this.product.style_type;
    const product2Type = this.doubleProduct.style_type;

    step.step_title = `${product1Type} ${step.step_title}`;
    stepToDuplicate.step_title = `${product2Type} ${stepToDuplicate.step_title}`;

    // set model ids
    step.model_id_field = `${this.formatMaterialId(product1Type)}${
      step.model_id_field
    }`;
    stepToDuplicate.model_id_field = `${this.formatMaterialId(product2Type)}${
      stepToDuplicate.model_id_field
    }`;

    // set new step ids
    step.step_id = generateStepId(step.step_title);
    stepToDuplicate.step_id = generateStepId(stepToDuplicate.step_title);

    step.options = this.updateDuplicateOptions(step);
    stepToDuplicate.options = this.updateDuplicateOptions(stepToDuplicate);

    return [step, stepToDuplicate];
  }

  /**
   * This function is responsible for comparing the passed in schema with the current schema
   * Each current schema step will be compared with the new one and logic will be done
   * to work out if one or both are required
   */
  private formatCustomiserSteps(newSchema) {
    const thisSchemaCopy = this.schema.map((step) => structuredClone(step));

    return thisSchemaCopy.reduce((acc, currentStep, index) => {
      const newStep = newSchema.find((step) => step.step_id === currentStep.step_id);

      // check if step is hidden on double config
      const stepHidden = currentStep?.hide_on_double_configuration || newStep?.hide_on_double_configuration;
      console.log('STEPS: ', { stepHidden, currentStep, newStep, acc: JSON.stringify(acc), index });

      if (stepHidden) return acc;
      // step exists in both schemas
      if (newStep) {
        // Check if the step should be shown for both Sheer and Blockout configurations
        const showDoubleStep =
          currentStep.duplicate_on_double_configuration ||
          newStep.duplicate_on_double_configuration;
        if (showDoubleStep) {
          const doubleSteps = this.formatDuplicateSteps(currentStep, newStep);

          acc.push(...doubleSteps);
          return acc;
        }
      }

      acc.push(currentStep);
      return acc;
    }, [])
    .map(step => {
      return {
        ...step,
        options: step.options.filter(option => !option.hide_on_double_configuration)
      }
    });
  }

  /**
   * MODEL LOGIC
   */
  private formatMaterialId(productStyleType:StyleType):MaterialId {

    if (productStyleType === 'Blockout') return 'BLOCKOUT';
    else if (productStyleType === 'Light Filter') return 'LIGHTFILTER';
    else if (productStyleType === 'Sunscreen') return 'SUNSCREEN';
    else return 'SHEER';
  }

  private singleProductMatFabHem(fabricFieldIds) {
    const Material = this.formatMaterialId(this.product.style_type);
    const fabricField = fabricFieldIds[0]; // For single products, there should only be a single field ID

    // get the first and only selectedColorOption
    const colorPickerStepId = this.colorPickerStepIds[0];
    const selectedColor = this.selectedColorOptions[colorPickerStepId];
    const HemType = selectedColor.hem;
    const colorId = selectedColor.option_id;
    return {
      HemType,
      Material,
      [fabricField]: colorId,
    };
  }

  private doubleProductMatFabHem(fabricFieldIds) {
    let productMaterial = this.formatMaterialId(this.product.style_type);
    const productColorOption =
      this.getSelectedColorOptionForMaterialId(productMaterial);
    const productFabricField = fabricFieldIds.find((fieldID) =>
      fieldID.includes(productMaterial)
    );

    let doubleProductMaterial = this.formatMaterialId(this.doubleProduct.style_type);
    const doubleProductColorOption = this.getSelectedColorOptionForMaterialId(doubleProductMaterial);
    const doubleProductFabricField = fabricFieldIds.find((fieldID) => fieldID.includes(doubleProductMaterial));

    /**
      If Cylindo updates the RDOUBLE Roller blinds to read 
      FABRICS SHEER/FABRICS BLOCKOUT then we can remove this stuff
    */
    const prodIsRoller = this.product.product_type_model_id === 'ROLLERBLIND';
    const prodIsBlockoutRoller = productMaterial === 'BLOCKOUT';
    const rollerFabField = prodIsBlockoutRoller ? 'FABRICS FRONT' : 'FABRICS REAR';
    const doubleRollerFabField = prodIsBlockoutRoller ? 'FABRICS REAR' : 'FABRICS FRONT';
    /*********************************************************************/

    return {
      [`${productMaterial}HemType`]: productColorOption.hem,
      [prodIsRoller ? rollerFabField : productFabricField]: productColorOption.option_id,
      [`${doubleProductMaterial}HemType`]: doubleProductColorOption.hem,
      [prodIsRoller ? doubleRollerFabField :doubleProductFabricField]: doubleProductColorOption.option_id,
    };
  }

  /**
   * PRICING
   */
  private useDiscountForProduct(productPricing: Pricing) {
    return productPricing.enable_sale_prices;
  }

  private useSalePriceMatrixForProduct(productPricing: Pricing) {
    return productPricing.use_sale_price_matrix;
  }

  private calcPriceForProduct(productPricing: Pricing) {
    const fabricPrice = this.calcPriceFromDimensionForProduct(productPricing);
    const fabricValue = !productPricing.enable_sale_prices || (productPricing.enable_sale_prices && productPricing.use_sale_price_matrix)
      ? fabricPrice
      : modifyPrice(fabricPrice, productPricing.discount);
    return fabricValue;
  }

  private calcPriceFromDimensionForProduct(productPricing: Pricing) {
    const pricingToUse =
      this.useDiscountForProduct(productPricing) &&
      this.useSalePriceMatrixForProduct(productPricing)
        ? productPricing.sale_price_matrix
        : productPricing.pricing;

    const widthTiers = Object.keys(pricingToUse);
    const activeWidthTier = widthTiers.find(
      (tier) => this.selected_dimensions.Width <= parseInt(tier)
    );
    const dropTiers = Object.keys(pricingToUse[activeWidthTier]);
    const activeDropTier = dropTiers.find(
      (tier) => this.selected_dimensions.Drop <= parseInt(tier)
    );
    return parseInt(pricingToUse[activeWidthTier][activeDropTier]);
  }

  /**
   * GETTERS
   * These functions return a variety of things
   * EG: Get an option by passign in an ID
   * EG: Get a step that an option ID belongs to
   * EG: Get all steps which contain Option Pickers
   * Plus many more
   */

  get firstSection() {
    return this.sections[0];
  }

  get calculatedPrice() {
    // There will be other price things we need to add in here
    // Additional options etc.
    const optionPrices = this.selectedOptionsWithCost
      .map((option) => {
        if (typeof option.cost === "string" && option.cost.includes("%")) {
          // Extract percentage value and calculate surcharge
          const percentageValue = parseFloat(option.cost);
          return (percentageValue / 100) * this.calcPriceForProduct(this.productPricing); 
        }
        return parseFloat(option.cost) || 0;
      })
      .reduce((acc, price) => acc + price, 0);

    const finalPrice = [
      this.calcPriceForProduct(this.productPricing),
      this.doubleProduct
        ? this.calcPriceForProduct(this.doubleProductPricing)
        : 0,
      optionPrices,
    ].reduce((acc, value) => (acc += value), 0);

    return finalPrice.toFixed(2);
  }

  private get selectedOptionComponents() {
    return this.selectedOptionIds.map((option_id) =>
      this.getOptionById(option_id)
    );
  }

  get selectedOptionIds() {
    // returns all ids of the selectied options
    return this.selectedOptionStepIds.map((stepId) => {
      return this.selectedOptions[stepId];
    });
  }

  get selectedOptionsWithCost() {
    return this.selectedOptionComponents?.filter((option) => {
      // only add selected option to list if its section is not hidden
      const isHidden = option?.parentSection?.closest('customiser-section.is-hidden') || false;
      return option?.cost && !isHidden;
    });
  }

  get allOptionsWithCost() {
    return this.optionPickers
      .map((optPicker) => optPicker.allOptions)
      .flat()
      .filter((option) => !!option.cost);
  }

  get selectedOptionStepIds() {
    // gets step IDs for all selected options
    return Object.keys(this.selectedOptions);
  }

  get allStepIds() {
    return this.sections.map((section) => section.step_id);
  }

  private get optionPickersWithModelIdFields() {
    return this.optionPickers.filter((optPicker) => !!optPicker.model_id_field);
  }

  private getSelectedColorOptionForMaterialId(MaterialId: MaterialId) {
    const colorPickerStepId = this.colorPickerStepIds.find((stepId) => {
      const selectedColorData = this.selectedColorOptions[stepId]; // this will be added as part of the color picker PR
      return this.formatMaterialId(selectedColorData.style_type) === MaterialId;
    });

    return this.selectedColorOptions[colorPickerStepId];
  }

  private get colorPickerStepIds() {
    return this.colorPickers.map((cPicker) => cPicker.step_id);
  }

  private get dimensionConfig(): DimensionSettings {
    // if doubleProduct get the higher of the two
    if (!this.isDoubleConfig)
      return {
        Width: {
          min: parseInt(this.product.min_width),
          max: parseInt(this.product.max_width),
        },
        Drop: {
          min: parseInt(this.product.min_drop),
          max: parseInt(this.product.max_drop),
        },
      };

    return {
      Width: {
        min: Math.max(this.product.min_width, this.doubleProduct.min_width),
        max: Math.min(this.product.max_width, this.doubleProduct.max_width),
      },
      Drop: {
        min: Math.max(this.product.min_drop, this.doubleProduct.min_drop),
        max: Math.min(this.product.max_drop, this.doubleProduct.max_drop),
      },
    };
  }

  private get activeSections() {
    return this.sections.filter(
      (section) => section.current_state === "Visible"
    );
  }

  get optionPickersWithMeasurementOptions() {
    return this.optionPickers.filter((optPicker) => {
      return (
        optPicker.closest("customiser-section:not(.is-hidden)") &&
        optPicker.querySelector("customiser-measurement-option")
      );
    });
  }

  get activeSectionsWithMeasurementOptions() {
    return this.activeSections.filter((optPicker) => {
      return optPicker.querySelector("customiser-measurement-option");
    });
  }

  get activeSectionsWithOptionPickers() {
    return this.stepsWithOptionPickers.filter(
      (section) => section.current_state === "Visible"
    );
  }

  get stepsWithOptionPickers() {
    return this.sections.filter((section) => {
      return section.querySelector("customiser-option-picker");
    });
  }

  get stepBeforeOptionPickers() {
    const firstOptionPicker = this.stepsWithOptionPickers[0];

    return firstOptionPicker.previousElementSibling;
  }

  private getStepById(stepId: string) {
    return this.sections.find((section) => section.step_id === stepId);
  }

  private getOptionById(optionId: string) {
    return this.optionPickers
      .map((optPicker) => optPicker.getOptionById(optionId))
      .find((option) => option);
  }

  private getOptionPickerForOptionId(optionId: string) {
    return this.optionPickers.find((optionPicker) => {
      const optionIdsForPicker = optionPicker.allOptions.map(
        (option) => option.option_id
      );
      return optionIdsForPicker.includes(optionId);
    });
  }

  private get formattedSummary() {
    // Get color data
    const colorData = Object.keys(this.selectedColorOptions).map(
      (color_step_id) => {
        return {
          step_id: color_step_id,
          title: this.getStepById(color_step_id).step_name,
          value: this.selectedColorOptions[color_step_id].option_title,
          image: `<img src="${this.selectedColorOptions[color_step_id].image}">`,
        };
      }
    );

    // Get size data
    const { width, drop, borderLength } = this.icon_ratio;
    const sizeData = {
      step_id: "CRTSZE",
      title: "Size",
      value: `${this.selected_dimensions.Width} × ${this.selected_dimensions.Drop}mm`,
      image: `<span class="ratio-icon" style="--icon-width:${
        width / 2
      }px;--icon-drop:${
        drop / 2
      }px;--icon-border-weight:2px;--icon-border-length:${
        borderLength / 2
      }px;"></span>`,
    };

    // Get option pickers' data
    const optionsData = this.activeSectionsWithOptionPickers
      .map((section) => {
        const option = this.selectedOptions[section.step_id];
        let stepImg, stepValue;

        if (option) {
          // Check if step contains input field
          if (typeof option === "object") {
            stepValue = Object.entries(option)
              .map(([key, value]) => {
                const val = value.value.trim();
                return val !== "" ? `${key}: ${val}` : null;
              })
              .filter((item) => item !== null)
              .join(", ");

            stepValue = stepValue.replaceAll("*", "").replaceAll(" (mm)", "");
          } else {
            const selectedOption = this.getOptionById(option);
            if (selectedOption?.icon != undefined) {
              stepImg = `<img src="${selectedOption?.icon}">`;
            }
            stepValue = selectedOption?.option_title;
          }

          return {
            step_id: section.step_id,
            title: section.step_name,
            value: stepValue,
            image: stepImg,
          };
        }
      })
      .filter(Boolean);

    return [...colorData, sizeData, ...optionsData];
  }

  /**
   * RE RENDER ELEMENTS
   */
  private createTooltip(props) {
    const { title, body, prompt, video } = props;

    const newTooltip = `
      <customiser-tooltip data="${prompt}">
        <customiser-modal title="${title}" content="<p>${escapeHtml(body)}</p>" video="${escapeHtml(video)}"></customiser-modal>
      </customiser-tooltip>
    `;

    return newTooltip;
  }

  private createSection(props) {
    const { index, stepName, stepTitle, additionalText, stepId, stepDescription, child, data={} } = props;
    const { tooltip_prompt, tooltip_modal_title, tooltip_modal_body, tooltip_modal_video } = data;
    const hasTooltip = tooltip_modal_body && tooltip_modal_title && tooltip_prompt;
  
    const newSection = `
      <customiser-section
        step-id="${stepId}"
        step-title="${stepTitle}"
        step-name="${stepName}"
        step-index="${index}"
        ${stepDescription ? 'class="has-description"' : ''}
        ${
          Object.keys(data).length
            ? `data="${JSON.stringify(data).replace(/"/gi, "&quot;")}"`
            : ""
        }
      >
        

        ${ stepDescription ? `<p class="o-customiser__step-description">${stepDescription}</p>` : '' }
        
        <div class="is-desktop">
          ${ hasTooltip ? this.createTooltip({
            title: tooltip_modal_title,
            prompt: tooltip_prompt,
            body: tooltip_modal_body,
            video: tooltip_modal_video
          }) : '' }
        </div>
        
        ${ child }

        <div class="is-mobile">
          ${ hasTooltip ? this.createTooltip({
            title: tooltip_modal_title,
            prompt: tooltip_prompt,
            body: tooltip_modal_body,
            video: tooltip_modal_video
          }) : '' }
        </div>

        ${ additionalText ? `<p>${additionalText}</p>` : '' }
        
      </customiser-section>
    `;

    return newSection;
  }

  private createOptionPicker(stepId, modelIdField = "", options) {
    const optionsMarkup = options
      .map((option) => {
        const elementTag = option.measurement_option
          ? "customiser-measurement-option"
          : "customiser-option";
        return `
        <${elementTag} step-id="${stepId}" model-id-field="${modelIdField}" data="${JSON.stringify(
          option
        ).replace(/"/gi, "&quot;")}"></${elementTag}>
      `;
      })
      .join("");

    const newOptionPicker = `
      <customiser-option-picker 
        step-id="${stepId}"
        model-id-field="${modelIdField}"
      >
        <fieldset>
          ${optionsMarkup}
        </fieldset>
      </customiser-option-picker>
    `;

    return newOptionPicker;
  }

  private createColors(stepId, style_type, colors) {
    return colors
      .map((colorVariant) => {
        const data = {
          title: colorVariant.title,
          colorImage: colorVariant.colorImage,
          sample_variant_availability: colorVariant.sample_variant_availability,
          sample_variant_id: colorVariant.sample_variant_id,
          option_id: colorVariant.option_id,
          hem: colorVariant.hem,
          style_type: style_type,
          default_variant_id: colorVariant.default_variant_id,
          image_1: colorVariant.image_1,
          image_2: colorVariant.image_2,
          image_3: colorVariant.image_3
        };
        return `
        <customiser-color 
          class="o-customiser__color-swatch"
          data="${JSON.stringify(data).replace(/"/gi, "&quot;")}"
          step-id="${stepId}"
        >
        
        </customiser-color>
      `;
      })
      .join("");
  }

  private createColorPicker(title, stepId, style_type, colors) {
    // This will be added when the color picker component has been built
    // newColorPicker.setAttribute('colors', product.colors)
    const newColorPicker = `
      <customiser-color-picker
        step-id="${stepId}"
        title="${title}"
      >
        <fieldset>
          ${this.createColors(stepId, style_type, colors)}
        </fieldset>
      </customiser-color-picker>
    `;

    return newColorPicker;
  }

  private renderOptionPickerSteps(
    steps,
    sectionToRenderAfter: CustomiserSection
  ) {
    const newOptionPickers = steps
      .map((step, step_index) => {
        const optionPicker = this.createOptionPicker(
          step.step_id,
          step.model_id_field,
          step.options
        );
        const section = this.createSection({
          index: step_index,
          stepName: step.step_title,
          stepTitle: step.step_title,
          stepDescription: step.step_description,
          stepId: step.step_id,
          child: optionPicker,
          data: step,
        });

        return section;
      })
      .join("");

    sectionToRenderAfter?.insertAdjacentHTML("afterend", newOptionPickers);
  }
}

if (!customElements.get("customiser-engine")) {
  customElements.define("customiser-engine", CustomiserEngine);
}
